import React, { useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { DollarSign, ArrowLeft } from 'lucide-react';
import { Button, Card, EmptyState, FilterBar, GroupByDropdown, ViewSwitcher, CalendarNavigation, ListDateCarousel, PrintButton } from '@/components/ui';
import { WeeklyView, MonthlyView } from '@/components/calendar';
import { useItemStore, useUIStore, useSettingsStore } from '@/stores';
import { ExpenseList } from './ExpenseList';
import { isRecurringItem, extendItemOccurrences } from '@perfin/shared';
import type { TabKey } from '@/stores/uiStore';

export function ExpensesPage() {
  const navigate = useNavigate();
  const transactions = useItemStore((state) => state.getTransactions());
  const { activeTab, setActiveTab, categoryFilters, pageFilters, viewMode, calendarOffset, groupBy } = useUIStore();
  const { currentUserId, getCategoriesForType, members } = useSettingsStore();
  const isAdmin = useSettingsStore((state) => state.currentUserRole === 'admin' || state.currentUserRole === 'superAdmin');

  const currentViewMode = viewMode?.expenses ?? 'card';
  const currentTab = activeTab?.expenses ?? 'today';
  const currentGroupBy = groupBy?.expenses ?? 'date';
  const pFilters = pageFilters?.expenses ?? { ownerFilter: null, statusFilter: null, priorityFilter: null };

  // Calendar views show all scheduled AND recurring items
  const isCalendarView = currentViewMode === 'weekly' || currentViewMode === 'monthly';
  const effectiveTab = isCalendarView ? 'allScheduledAndRecurring' : currentTab;

  // Bug #2: Auto-switch groupBy when 'date' is invalid for All Recurring tab
  const { setGroupBy } = useUIStore();
  React.useEffect(() => {
    if (currentTab === 'allRecurring' && currentGroupBy === 'date') {
      setGroupBy('expenses', 'category');
    }
  }, [currentTab, currentGroupBy, setGroupBy]);

  // Bug #5: Validate owner filter - if references non-existent user, reset to current user
  const { setOwnerFilter } = useUIStore();
  React.useEffect(() => {
    if (pFilters.ownerFilter && members.length > 0) {
      const ownerExists = members.some(m => m.userId === pFilters.ownerFilter);
      if (!ownerExists && currentUserId) {
        setOwnerFilter('expenses', currentUserId);
      }
    }
  }, [pFilters.ownerFilter, members, currentUserId, setOwnerFilter]);

  const tabs: { key: TabKey; label: string }[] = [
    { key: 'allScheduled', label: 'All Scheduled' },
    { key: 'allUnscheduled', label: 'All Unscheduled' },
    { key: 'allRecurring', label: 'All Recurring' },
    { key: 'pastWeek', label: 'Past Week' },
    { key: 'today', label: 'Today' },
    { key: 'nextWeek', label: 'Next Week' },
  ];

  // Get reference date from store (for Today tab carousel - affects all time-based tabs)
  const getReferenceDate = useUIStore((state) => state.getReferenceDate);
  const referenceDate = getReferenceDate('expenses');

  const filteredExpenses = useMemo(() => {
    // Use referenceDate for time-based tabs (Today carousel can change this)
    const refDate = new Date(referenceDate.getFullYear(), referenceDate.getMonth(), referenceDate.getDate());
    const refDatePlusOne = new Date(refDate.getTime() + 24 * 60 * 60 * 1000);
    const sevenDaysBefore = new Date(refDate.getTime() - 7 * 24 * 60 * 60 * 1000);
    const sevenDaysAfter = new Date(refDate.getTime() + 7 * 24 * 60 * 60 * 1000);

    const parseDate = (d: unknown): Date | null => {
      if (!d) return null;
      if (typeof (d as { toDate?: () => Date }).toDate === 'function') {
        return (d as { toDate: () => Date }).toDate();
      }
      if (d instanceof Date) return d;
      if (typeof d === 'object' && d !== null && 'seconds' in d) {
        return new Date((d as { seconds: number }).seconds * 1000);
      }
      return new Date(d as string | number);
    };

    let result = transactions;

    // 1. Apply filters FIRST (category, status, priority)
    const filter = categoryFilters.expenses;
    if (filter.categoryId) {
      result = result.filter((t) => t.category === filter.categoryId);
    }
    if (filter.subCategoryId) {
      result = result.filter((t) => t.subCategory === filter.subCategoryId);
    }
    if (pFilters?.itemGroupFilter) {
      result = result.filter((t) => t.itemGroup === pFilters.itemGroupFilter);
    }

    // Status filter: for time-based tabs, only apply to non-recurring items
    // (recurring items will be filtered by occurrence status in ExpenseList)
    const isTimeBased = effectiveTab === 'pastWeek' || effectiveTab === 'today' || effectiveTab === 'nextWeek';
    if (pFilters.statusFilter) {
      result = result.filter((t) => {
        if (isTimeBased && isRecurringItem(t)) {
          return true; // Let ExpenseList filter by occurrence status
        }
        return t.status === pFilters.statusFilter;
      });
    }

    if (pFilters.priorityFilter) {
      result = result.filter((t) => t.priority === pFilters.priorityFilter);
    }

    // Owner filtering
    if (pFilters?.ownerFilter) {
      result = result.filter((t) => t.owner === pFilters.ownerFilter);
    } else if (!isAdmin && currentUserId) {
      // Standard users without owner filter: show items they own OR created
      result = result.filter((t) => t.owner === currentUserId || t.createdBy === currentUserId);
    }

    // 2. Apply tab (time) filter on the result (use effectiveTab for calendar view compatibility)
    switch (effectiveTab) {
      case 'allScheduledAndRecurring':
        // For calendar views: include scheduled non-recurring items AND all recurring items
        result = result.filter((t) => {
          if (isRecurringItem(t)) return true; // Include recurring
          const dueDate = parseDate(t.dueDate);
          return !!dueDate; // Include scheduled non-recurring
        });
        break;

      case 'allScheduled':
        // NON-RECURRING items with dueDate
        result = result.filter((t) => {
          if (isRecurringItem(t)) return false;
          const dueDate = parseDate(t.dueDate);
          return !!dueDate;
        });
        break;

      case 'allUnscheduled':
        // NON-RECURRING items with no due date
        result = result.filter((t) => {
          if (isRecurringItem(t)) return false;
          const dueDate = parseDate(t.dueDate);
          return !dueDate;
        });
        break;

      case 'allRecurring':
        // All recurring items - NO tile replication
        result = result.filter((t) => isRecurringItem(t));
        break;

      case 'pastWeek':
        // Scheduled + Recurring with occurrences in range (excludes unscheduled)
        result = result.filter((t) => {
          if (isRecurringItem(t)) {
            const occurrences = (t as unknown as { occurrences?: Array<{ date: unknown }> }).occurrences || [];
            return occurrences.some(occ => {
              const occDate = parseDate(occ.date);
              return occDate && occDate >= sevenDaysBefore && occDate < refDate;
            });
          }
          const dueDate = parseDate(t.dueDate);
          return dueDate && dueDate >= sevenDaysBefore && dueDate < refDate;
        });
        break;

      case 'today':
        // Scheduled + Recurring with occurrences on refDate (excludes unscheduled)
        result = result.filter((t) => {
          if (isRecurringItem(t)) {
            const occurrences = (t as unknown as { occurrences?: Array<{ date: unknown }> }).occurrences || [];
            return occurrences.some(occ => {
              const occDate = parseDate(occ.date);
              return occDate && occDate >= refDate && occDate < refDatePlusOne;
            });
          }
          const dueDate = parseDate(t.dueDate);
          return dueDate && dueDate >= refDate && dueDate < refDatePlusOne;
        });
        break;

      case 'nextWeek':
        // Scheduled + Recurring with occurrences in range (excludes unscheduled)
        result = result.filter((t) => {
          if (isRecurringItem(t)) {
            const occurrences = (t as unknown as { occurrences?: Array<{ date: unknown }> }).occurrences || [];
            return occurrences.some(occ => {
              const occDate = parseDate(occ.date);
              return occDate && occDate >= refDatePlusOne && occDate <= sevenDaysAfter;
            });
          }
          const dueDate = parseDate(t.dueDate);
          return dueDate && dueDate >= refDatePlusOne && dueDate <= sevenDaysAfter;
        });
        break;
    }

    return result;
  }, [transactions, effectiveTab, pFilters, categoryFilters.expenses, currentUserId, isAdmin, referenceDate]);

  const handleAddTransaction = () => navigate('/items/new?mode=create&type=transaction');

  // Handler for extending occurrences when navigating beyond 180-day horizon
  const handleNavigateBeyondHorizon = useCallback(async (targetDate: Date) => {
    const recurringIds = transactions
      .filter((t) => isRecurringItem(t))
      .map((t) => t.id);

    if (recurringIds.length > 0) {
      try {
        await extendItemOccurrences(recurringIds, targetDate);
      } catch (error) {
        console.error('Failed to extend occurrences:', error);
      }
    }
  }, [transactions]);

  // Helper functions for PDF generation
  const transactionCategories = getCategoriesForType('transaction');
  const getCategoryName = (categoryId: string) => {
    const cat = transactionCategories.find((c) => c.id === categoryId);
    return cat?.name || categoryId;
  };
  const getOwnerName = (ownerId: string) => {
    const member = members.find((m) => m.userId === ownerId);
    return member?.displayName || ownerId;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/')}
            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <h1 className="text-2xl font-bold text-gray-900">Transactions</h1>
          <PrintButton
            title="Transactions"
            items={filteredExpenses}
            getCategoryName={getCategoryName}
            getOwnerName={getOwnerName}
          />
        </div>
      </div>

      <div className="flex items-center justify-between flex-wrap gap-4">
        <FilterBar itemType="transaction" pageKey="expenses" hideStatusFilter={currentTab === 'allRecurring'} />
        <hr className="w-full border-gray-300 md:hidden" />
        <div className="flex items-center gap-3 flex-wrap md:flex-nowrap">
          {currentViewMode === 'card' && <GroupByDropdown pageKey="expenses" showInfoTooltip excludeOptions={currentTab === 'allRecurring' ? ['date'] : []} />}
          <Button onClick={handleAddTransaction}>
            Add Transaction
          </Button>
          <ViewSwitcher pageKey="expenses" />
        </div>
      </div>

      <div className="flex items-center justify-between border-b border-gray-200">
        <div className="flex gap-1 flex-wrap md:flex-nowrap">
          {/* In calendar view, show single "All Scheduled and Recurring" tab */}
          {isCalendarView ? (
            <button className="px-4 py-2 text-sm font-medium border-b-2 border-primary-600 text-primary-600">
              All Scheduled and Recurring
            </button>
          ) : (
            tabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab('expenses', tab.key)}
                className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                  currentTab === tab.key
                    ? 'border-primary-600 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab.label}
              </button>
            ))
          )}
        </div>
        {currentViewMode === 'card' && (currentTab === 'pastWeek' || currentTab === 'today' || currentTab === 'nextWeek') && (
          <ListDateCarousel pageKey="expenses" />
        )}
        {currentViewMode !== 'card' && (
          <CalendarNavigation
            pageKey="expenses"
            mode={currentViewMode}
            onNavigateBeyondHorizon={handleNavigateBeyondHorizon}
          />
        )}
      </div>

      {currentViewMode === 'card' && (
        <>
          {filteredExpenses.length === 0 ? (
            <Card>
              <EmptyState
                icon={<DollarSign className="h-12 w-12" />}
                title="No transactions this month"
                description="Start tracking your transactions by adding your first one"
                action={{ label: 'Add Transaction', onClick: handleAddTransaction }}
              />
            </Card>
          ) : (
            <ExpenseList expenses={filteredExpenses} groupBy={currentGroupBy} currentTab={currentTab} hideStatusToggle={currentTab === 'allRecurring'} referenceDate={referenceDate} statusFilter={pFilters.statusFilter} />
          )}
        </>
      )}

      {currentViewMode === 'weekly' && (
        <WeeklyView
          items={filteredExpenses}
          offset={calendarOffset.expenses.weekly}
          basePath="/transactions"
        />
      )}

      {currentViewMode === 'monthly' && (
        <MonthlyView
          items={filteredExpenses}
          offset={calendarOffset.expenses.monthly}
          basePath="/transactions"
        />
      )}
    </div>
  );
}
